// AriveSyncPanel — ticket 134 P1. Owner-only, read-only view of the Arive
// webhook sync: health pill, last event, 24h counts and rejected calls.
// Counts and timestamps only; the endpoint never returns record values.
import React, { useEffect, useState } from 'react';
import { Loader2, Info } from 'lucide-react';
import { api } from '../api';
import KpiTile from './KpiTile';
import { fromApi } from '../kit/time';

const relTime = (iso) => {
  if (!iso) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - fromApi(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const PILLS = {
  ok: { label: 'Healthy', bg: '#DCFCE7', color: '#166534' },
  error: { label: 'Last event failed', bg: '#FEE2E2', color: '#991B1B' },
  capture: { label: 'Capture only', bg: '#FEF3C7', color: '#92400E' },
  idle: { label: 'No events yet', bg: '#F1F5F9', color: '#64748B' },
  off: { label: 'Not configured', bg: '#F1F5F9', color: '#64748B' },
};

function pillFor(data) {
  if (!data.configured) return PILLS.off;
  if (data.mode === 'capture') return PILLS.capture;
  if (!data.health) return PILLS.idle;
  return data.health.last_status === 'error' ? PILLS.error : PILLS.ok;
}

export default function AriveSyncPanel() {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    let alive = true;
    api.get('/super/integrations/arive')
      .then((r) => { if (alive) setState({ data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ data: null, error: e?.response?.data?.detail || 'Could not load Arive sync status' }); });
    return () => { alive = false; };
  }, []);
  const { data, error } = state;

  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-8" data-testid="arive-sync-panel">
      <div className="px-5 py-4 border-b border-slate-100">
        <h2 className="font-serif text-lg font-bold leading-tight" style={{ color: '#070B5F' }}>Arive sync</h2>
        <p className="text-[12px] text-slate-500 mt-0.5">Loans pushed from Arive through Zapier. Read-only.</p>
      </div>
      <div className="p-5">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="arive-sync-error">{String(error)}</div>
        )}
        {!error && !data && (
          <div className="text-slate-500 flex items-center gap-2 text-sm" data-testid="arive-sync-loading">
            <Loader2 size={16} className="animate-spin" /> Loading Arive sync status…
          </div>
        )}
        {data && <AriveSyncBody data={data} />}
      </div>
    </section>
  );
}

function AriveSyncBody({ data }) {
  const pill = pillFor(data);
  const h = data.health;
  const runs = data.runs_24h || { ok: 0, error: 0 };
  return (
    <div className="space-y-4">
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
        style={{ background: pill.bg, color: pill.color }}
        data-testid="arive-sync-state-pill"
      >
        {pill.label}
      </span>

      {data.configured && (
        <>
          <p className="text-sm text-slate-600" data-testid="arive-sync-last-line">
            {h?.last_event_at
              ? `Last event: ${relTime(h.last_event_at)} (${h.last_event_kind || 'unknown'})`
              : 'Last event: never'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KpiTile
              label="Events total"
              value={h?.events_total ?? 0}
              sub={`${h?.errors_total ?? 0} failed`}
              icon={Info}
              testid="arive-kpi-events"
            />
            <KpiTile
              label="Runs, 24h"
              value={runs.ok + runs.error}
              sub={`${runs.ok} ok, ${runs.error} failed`}
              icon={Info}
              testid="arive-kpi-runs"
            />
            <KpiTile
              label="Rejected, 24h"
              value={data.rejections_24h ?? 0}
              sub="bad or missing secret"
              icon={Info}
              testid="arive-kpi-rejections"
            />
            <KpiTile
              label="Last error"
              value={h?.last_error_class || 'none'}
              sub={h?.last_error_at ? relTime(h.last_error_at) : 'none'}
              icon={Info}
              testid="arive-kpi-last-error"
            />
          </div>
        </>
      )}
    </div>
  );
}
